import { useLayoutEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { createPortal } from "react-dom"
import { Button } from "@/components/ui/button"
import { Upload } from "lucide-react"
import { useAppSelector } from "@/reducers/hooks"
import { selectTabs, TAB_TYPES } from "@/reducers/tabsSlice"
import { useStatsData } from "./data/useStatsData"
import { DateRangePicker } from "@/components/ui/DateRangePicker"
import { DriverFilter } from "@/components/deliveryOrderReport/DriverFilter"
import { SummaryTab } from "./tabs/SummaryTab"
import { BiuroTab } from "./tabs/BiuroTab"
import { FlotaTab } from "./tabs/FlotaTab"
import { ZleceniaTab } from "./tabs/ZleceniaTab"
import { cn } from "@/lib/utils"

type TabId = "podsumowanie" | "biuro" | "flota" | "zlecenia"

const tabs = [
  { id: "podsumowanie", labelKey: "planner.stats.tabs.summary" },
  { id: "biuro", labelKey: "planner.stats.tabs.office" },
  { id: "flota", labelKey: "planner.stats.tabs.fleet" },
  { id: "zlecenia", labelKey: "planner.stats.tabs.orders" },
] as const

export function StatsPage() {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [activeTabId, setActiveTabId] = useState<TabId>("podsumowanie")
  const { activeTab } = useAppSelector(selectTabs)

  // Filter state
  const [dateRange, setDateRange] = useState<{ from: Date; to: Date } | null>(
    () => {
      const today = new Date()
      const thirtyDaysAgo = new Date()
      thirtyDaysAgo.setDate(today.getDate() - 30)
      return { from: thirtyDaysAgo, to: today }
    },
  )
  const [selectedDriver, setSelectedDriver] = useState<string>("all-drivers")

  // Fetch stats data
  const { data, isLoading, error } = useStatsData({
    dateRange,
    driverId: selectedDriver,
  })

  useLayoutEffect(() => {
    if (activeTab === TAB_TYPES.STATS) {
      setIsOpen(true)
    } else {
      setIsOpen(false)
    }
  }, [activeTab])

  const handleExport = () => {
    // TODO: Implement export functionality when API is ready
    console.log("Export clicked")
  }

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-base text-[#737373]">
            {t("planner.stats.loading")}
          </div>
        </div>
      )
    }

    if (error) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-base text-[#dc2626]">
            {t("planner.stats.error", { error })}
          </div>
        </div>
      )
    }

    if (!data) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-base text-[#737373]">
            {t("planner.stats.noData")}
          </div>
        </div>
      )
    }

    switch (activeTabId) {
      case "podsumowanie":
        return (
          <SummaryTab
            ordersSummary={data.ordersSummary}
            distanceComparison={data.distanceComparison}
            roadCostsComparison={data.roadCostsComparison}
            fuelComparison={data.fuelComparison}
            loadedKmComparison={data.loadedKmComparison}
          />
        )
      case "biuro":
        return <BiuroTab dispatchers={data.dispatchers} />
      case "flota":
        return <FlotaTab trucks={data.fleetTrucks} />
      case "zlecenia":
        return <ZleceniaTab orders={data.orderDocuments} />
      default:
        return null
    }
  }

  if (!isOpen) return null

  return createPortal(
    <div className="fixed inset-0 left-[49px] z-50 flex w-[100vw-48px] items-center justify-center bg-sidebar">
      <div className="relative h-full w-full">
        <div className="h-full w-full overflow-auto">
          <div className="p-6">
            {/* Header */}
            <div className="mb-6 flex items-center justify-between">
              <h1 className="text-3xl font-bold tracking-tight text-[#0a0a0a]">
                {t("planner.stats.title")}
              </h1>
              <div className="flex items-center gap-3">
                <DateRangePicker
                  dateRange={dateRange}
                  setDateRange={setDateRange}
                  maxDays={31}
                />
                <DriverFilter
                  selectedChat={selectedDriver}
                  onChatChange={setSelectedDriver}
                />
                <Button
                  variant="outline"
                  onClick={handleExport}
                  disabled={isLoading}
                  className="gap-2 border-[#e5e5e5] bg-white text-sm font-medium text-[#0a0a0a] shadow-sm hover:bg-[#f5f5f5]"
                >
                  {t("planner.stats.exportAs")}
                  <Upload className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Tab navigation */}
            <div className="mb-6">
              <div className="inline-flex rounded-lg bg-[#f5f5f5] p-[3px]">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTabId(tab.id)}
                    className={cn(
                      "rounded-md px-4 py-2 text-sm font-medium transition-colors",
                      activeTabId === tab.id
                        ? "bg-white text-[#0a0a0a] shadow-sm"
                        : "text-[#0a0a0a] hover:text-[#0a0a0a]",
                    )}
                  >
                    {t(tab.labelKey)}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab content */}
            {renderContent()}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
